import { uiText } from '../../utils/i18n';
import { useState, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import styles from './MemoryPanel.module.css';

interface MemoryFile {
  name: string;
  content: string;
  size: number;
}

interface SharedDoc {
  id: string;
  title: string;
  path: string;
  tags: string[];
  created_by: string;
  updated_at: string;
  content?: string;
}

interface SharedFolder {
  id: string;
  name: string;
  path: string;
  children: SharedFolder[];
  document_count: number;
}

interface MemoryPanelProps {
  botName: string;
}

type ActiveSection = 'private' | 'shared';

export function MemoryPanel({ botName }: MemoryPanelProps) {
  return (
    <div className={styles.container}>
      <PrivateMemory botName={botName} />
    </div>
  );
}

/* ========== Private Memory (Codex workspace memory) ========== */

function PrivateMemory({ botName }: { botName: string }) {
  const [files, setFiles] = useState<MemoryFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedFile, setSelectedFile] = useState<MemoryFile | null>(null);
  const token = localStorage.getItem('novax:token') || '';

  useEffect(() => {
    setLoading(true);
    setSelectedFile(null);
    fetch(`/api/agent-memory/${encodeURIComponent(botName)}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => {
        setFiles(data.files || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [botName, token]);

  if (loading) {
    return <div className={styles.loading}>{uiText("加载中...")}</div>;
  }

  if (selectedFile) {
    return (
      <div className={styles.fileView}>
        <div className={styles.fileViewHeader}>
          <button className={styles.backBtn} onClick={() => setSelectedFile(null)}>
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            {uiText("返回")}
          </button>
          <span className={styles.fileViewName}>{selectedFile.name}</span>
        </div>
        <div className={styles.fileViewBody}>
          <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]}>
            {selectedFile.content}
          </ReactMarkdown>
        </div>
      </div>
    );
  }

  if (files.length === 0) {
    return (
      <div className={styles.empty}>
        <div className={styles.emptyIcon}>🧠</div>
        <div className={styles.emptyTitle}>{uiText("暂无记忆")}</div>
        <div className={styles.emptyHint}>{botName} {uiText("还没有积累私有记忆")}</div>
      </div>
    );
  }

  // Show MEMORY.md content inline, other files as clickable list
  const mainMemory = files.find((f) => f.name === 'MEMORY.md');
  const otherFiles = files.filter((f) => f.name !== 'MEMORY.md');

  return (
    <div className={styles.privateBody}>
      {mainMemory && (
        <div className={styles.mainMemory}>
          <div className={styles.mainMemoryHeader}>
            <span className={styles.mainMemoryIcon}>🧠</span>
            <span className={styles.mainMemoryTitle}>MEMORY.md</span>
          </div>
          <div className={styles.markdownContent}>
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]}>
              {mainMemory.content}
            </ReactMarkdown>
          </div>
        </div>
      )}

      {otherFiles.length > 0 && (
        <div className={styles.subFiles}>
          <div className={styles.subFilesHeader}>
            {uiText("详细记忆文件")}
            <span className={styles.subFilesCount}>{otherFiles.length}</span>
          </div>
          <div className={styles.subFilesList}>
            {otherFiles.map((f) => (
              <button key={f.name} className={styles.subFileRow} onClick={() => setSelectedFile(f)}>
                <span className={styles.subFileIcon}>📄</span>
                <span className={styles.subFileName}>{f.name.replace(/\.md$/, '')}</span>
                <svg className={styles.chevron} width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ========== Shared Memory (MetaMemory) ========== */

function SharedMemory() {
  const [docs, setDocs] = useState<SharedDoc[]>([]);
  const [folders, setFolders] = useState<SharedFolder | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SharedDoc[] | null>(null);
  const [selectedDoc, setSelectedDoc] = useState<SharedDoc | null>(null);
  const token = localStorage.getItem('novax:token') || '';

  // Use the proxy route which auto-injects workspace_id for tenant isolation
  const memoryUrl = '/memory/api';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [docsRes, foldersRes] = await Promise.all([
        fetch(`${memoryUrl}/documents`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${memoryUrl}/folders`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (docsRes.ok) setDocs(await docsRes.json());
      if (foldersRes.ok) setFolders(await foldersRes.json());
    } catch { /* ignore */ }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) {
      setSearchResults(null);
      return;
    }
    try {
      const res = await fetch(`${memoryUrl}/search?q=${encodeURIComponent(searchQuery)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setSearchResults(await res.json());
    } catch { /* ignore */ }
  }, [searchQuery, token]);

  const fetchDoc = useCallback(async (docId: string) => {
    try {
      const res = await fetch(`${memoryUrl}/documents/${docId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const doc = await res.json();
        setSelectedDoc(doc);
      }
    } catch { /* ignore */ }
  }, [token]);

  if (selectedDoc) {
    return (
      <div className={styles.fileView}>
        <div className={styles.fileViewHeader}>
          <button className={styles.backBtn} onClick={() => setSelectedDoc(null)}>
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            {uiText("返回")}
          </button>
          <span className={styles.fileViewName}>{selectedDoc.title}</span>
          {selectedDoc.tags?.length > 0 && (
            <div className={styles.docTags}>
              {selectedDoc.tags.map((t) => (
                <span key={t} className={styles.docTag}>{t}</span>
              ))}
            </div>
          )}
        </div>
        <div className={styles.fileViewBody}>
          <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]}>
            {selectedDoc.content || ''}
          </ReactMarkdown>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className={styles.loading}>{uiText("加载中...")}</div>;
  }

  const displayDocs = searchResults || docs;

  return (
    <div className={styles.sharedBody}>
      <div className={styles.searchBar}>
        <input
          className={styles.searchInput}
          type="text"
          placeholder={uiText("搜索知识库...")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
        />
        <button className={styles.searchBtn} onClick={handleSearch}>
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        </button>
      </div>

      {displayDocs.length === 0 ? (
        <div className={styles.empty}>
          <div className={styles.emptyIcon}>📚</div>
          <div className={styles.emptyTitle}>{uiText("知识库为空")}</div>
          <div className={styles.emptyHint}>{uiText("在对话中让 agent \"记住\"重要信息")}</div>
        </div>
      ) : (
        <div className={styles.docList}>
          {displayDocs.map((doc) => (
            <button
              key={doc.id}
              className={styles.docRow}
              onClick={() => fetchDoc(doc.id)}
            >
              <span className={styles.docIcon}>📝</span>
              <div className={styles.docInfo}>
                <span className={styles.docTitle}>{doc.title}</span>
                <span className={styles.docMeta}>
                  {doc.path}
                  {doc.created_by && ` · by ${doc.created_by}`}
                </span>
              </div>
              {doc.tags?.length > 0 && (
                <div className={styles.docTagsSmall}>
                  {doc.tags.slice(0, 2).map((t) => (
                    <span key={t} className={styles.docTagSmall}>{t}</span>
                  ))}
                </div>
              )}
              <svg className={styles.chevron} width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
